import React, { useState, useEffect } from 'react';
import { apiJson } from '../apiClient';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Panel } from '../../crm/components/ui/Panel';
import { Badge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { EmptyState } from '../../crm/components/ui/EmptyState';
import { AttentionPanel } from '../../crm/components/ui/AttentionPanel';
import { Metric } from '../../crm/components/ui/Metric';

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');

function categoriaAtividade(action) {
  if (action.startsWith('TAREFA_')) return 'accent';
  if (action.startsWith('RECEIVABLE') || action.startsWith('PAYABLE') || action.startsWith('PAYMENT') || action.startsWith('BILLING') || action.startsWith('RECURRENCE')) return 'positive';
  if (action.startsWith('CLIENTE_')) return 'warning';
  if (action.startsWith('PROJETO_')) return 'accent';
  if (action.startsWith('ATIVO_')) return 'neutral';
  if (action.startsWith('DOCUMENTO_')) return 'neutral';
  return 'neutral';
}

function saudacao() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

// Dashboard editorial (fase 3 do redesign, §11/§20) — a versão anterior
// era uma pilha uniforme de KpiCards (mesma hierarquia visual pra tudo:
// MRR, itens de estoque, tarefas atrasadas, todos do mesmo tamanho). Esta
// versão separa em duas colunas de propósito diferente logo abaixo da
// saudação — "Requer atenção" (uma lista única e priorizada do que
// precisa de ação, cruzando financeiro/projetos/estoque/patrimônio) e
// "Visão financeira" (números editoriais grandes, não células de grade).
// Os módulos individuais (Projetos/Estoque/Patrimônio) continuam abaixo,
// mas como detalhe secundário — não competem mais com o topo da tela.
function Painel({ modulosHabilitados, permissaoClientes, onNavegar, nomeUsuario }) {
  const [financeiro, setFinanceiro] = useState(null);
  const [projetos, setProjetos] = useState(null);
  const [estoque, setEstoque] = useState(null);
  const [patrimonio, setPatrimonio] = useState(null);
  const [atividade, setAtividade] = useState(null);
  const [showVencidos, setShowVencidos] = useState(false);
  const [vencidos, setVencidos] = useState(null);
  const [showAtrasadas, setShowAtrasadas] = useState(false);
  const [atrasadas, setAtrasadas] = useState(null);

  useEffect(() => {
    if (modulosHabilitados?.financeiro) apiJson('/financeiro/dashboard/resumo').then((r) => { if (r.status === 'ok') setFinanceiro(r); });
    if (modulosHabilitados?.projetos) apiJson('/projetos/dashboard').then((r) => { if (r.status === 'ok') setProjetos(r); });
    if (modulosHabilitados?.estoque) apiJson('/estoque/dashboard').then((r) => { if (r.status === 'ok') setEstoque(r); });
    if (modulosHabilitados?.patrimonio) apiJson('/patrimonio/dashboard').then((r) => { if (r.status === 'ok') setPatrimonio(r); });
    apiJson('/auditoria?limite=12').then((r) => { if (r.status === 'ok') setAtividade(r.lista); });
  }, [modulosHabilitados]);

  const toggleVencidos = () => {
    setShowVencidos((v) => !v);
    if (!vencidos) apiJson('/financeiro/receivables?status=VENCIDO').then((r) => { if (r.status === 'ok') setVencidos(r.lista); });
  };
  const toggleAtrasadas = () => {
    setShowAtrasadas((v) => !v);
    if (!atrasadas) apiJson('/projetos/dashboard/tarefas-atrasadas').then((r) => { if (r.status === 'ok') setAtrasadas(r.lista); });
  };

  const nenhumModulo = !modulosHabilitados?.financeiro && !modulosHabilitados?.projetos && !modulosHabilitados?.estoque && !modulosHabilitados?.patrimonio;

  const acoes = [
    { label: 'Cliente', destino: 'clientes', visivel: permissaoClientes },
    { label: 'Projeto', destino: 'projetos', visivel: modulosHabilitados?.projetos },
    { label: 'Produto', destino: 'estoque', visivel: modulosHabilitados?.estoque },
    { label: 'Ativo', destino: 'patrimonio', visivel: modulosHabilitados?.patrimonio },
    { label: 'Documento', destino: 'documentos', visivel: true },
  ].filter((a) => a.visivel);

  const itensAtencao = [
    { id: 'vencidos', label: 'Recebíveis vencidos', count: financeiro?.clientes?.vencidas || 0, tone: 'danger', onClick: () => onNavegar('financeiro') },
    { id: 'atrasadas', label: 'Tarefas atrasadas', count: projetos?.tarefas_atrasadas || 0, tone: 'warning', onClick: () => onNavegar('projetos') },
    { id: 'estoque-baixo', label: 'Itens com estoque baixo', count: estoque?.estoque_baixo || 0, tone: 'warning', onClick: () => onNavegar('estoque') },
    { id: 'manutencao', label: 'Ativos em manutenção', count: patrimonio?.em_manutencao || 0, tone: 'warning', onClick: () => onNavegar('patrimonio') },
    { id: 'defeituosos', label: 'Ativos defeituosos', count: patrimonio?.defeituosos || 0, tone: 'danger', onClick: () => onNavegar('patrimonio') },
    { id: 'criticos', label: 'Projetos críticos', count: projetos?.projetos_criticos || 0, tone: 'danger', onClick: () => onNavegar('projetos') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--crm-space-3)' }}>
        <div>
          {/* Saudação na fonte de display — é a primeira linha do produto que
              a pessoa lê todo dia; classe em vez de estilo inline para o
              tamanho responder ao viewport. */}
          <h2 className="crm-dashboard-greeting">{saudacao()}{nomeUsuario ? `, ${nomeUsuario}` : ''}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--crm-text-sm)', color: 'var(--crm-text-secondary)' }}>Aqui está o que precisa da sua atenção hoje.</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          {acoes.map((a) => <Button key={a.label} size="sm" variant="outline" onClick={() => onNavegar(a.destino)}>+ {a.label}</Button>)}
        </div>
      </div>

      {nenhumModulo ? (
        <EmptyState title="Nenhum módulo comercial habilitado ainda" description="Clientes e Documentos estão sempre disponíveis." />
      ) : (
        <>
          <div className="crm-dashboard-hero-grid">
            <Panel title="Requer atenção" flush>
              <AttentionPanel items={itensAtencao} />
            </Panel>
            {financeiro && (
              <Panel title="Visão financeira">
                <div className="crm-dashboard-metrics-row" style={{ padding: 0 }}>
                  <Metric label="MRR" value={fmtBRL(financeiro.mrr)} />
                  <Metric label="Resultado" value={fmtBRL(financeiro.resultado_liquido)} tone={parseFloat(financeiro.resultado_liquido) < 0 ? 'danger' : 'positive'} />
                </div>
                <div style={{ marginTop: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="crm-form-hint">Receita do mês</span>
                    <strong>{fmtBRL(financeiro.receitas.recebidas)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="crm-form-hint">A receber</span>
                    <strong>{fmtBRL(financeiro.receitas.pendentes)}</strong>
                  </div>
                  <div className="clickable" style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={toggleVencidos}>
                    <span className="crm-form-hint">Vencido {showVencidos ? '▾' : '▸'}</span>
                    <strong style={{ color: parseFloat(financeiro.receitas.vencidas) > 0 ? 'var(--crm-danger)' : undefined }}>{fmtBRL(financeiro.receitas.vencidas)}</strong>
                  </div>
                </div>
              </Panel>
            )}
          </div>

          {showVencidos && (
            <Panel title="Contas vencidas" flush>
              {vencidos === null ? (
                <div style={{ padding: 'var(--crm-space-4)' }} className="crm-form-hint">Carregando...</div>
              ) : vencidos.length === 0 ? (
                <EmptyState title="Nenhuma conta vencida" />
              ) : (
                <div className="crm-table-wrap">
                  <table className="crm-table">
                    <thead><tr><th>Descrição</th><th>Cliente</th><th>Vencimento</th><th>Valor</th></tr></thead>
                    <tbody>
                      {vencidos.map((r) => (
                        <tr key={r.id} className="clickable" onClick={() => onNavegar('financeiro')}>
                          <td className="crm-table-primary">{r.descricao}</td>
                          <td>{r.cliente_nome || '—'}</td>
                          <td>{fmtDate(r.vencimento)}</td>
                          <td>{fmtBRL(r.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}

          <div>
            <h3 style={{ margin: '0 0 var(--crm-space-3)', fontSize: 'var(--crm-text-sm)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 'var(--crm-tracking-label)', color: 'var(--crm-text-tertiary)' }}>Detalhes por área</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--crm-space-4)' }}>
              {projetos && (
                <Panel title="Projetos">
                  <KpiGrid>
                    <KpiCard label="Ativos" value={projetos.projetos_ativos} />
                    <KpiCard label="Críticos" value={projetos.projetos_criticos} tone={projetos.projetos_criticos > 0 ? 'danger' : undefined} />
                    <div className="clickable" style={{ cursor: 'pointer' }} onClick={toggleAtrasadas}>
                      <KpiCard label={`Tarefas atrasadas ${showAtrasadas ? '▾' : '▸'}`} value={projetos.tarefas_atrasadas} tone={projetos.tarefas_atrasadas > 0 ? 'danger' : undefined} />
                    </div>
                  </KpiGrid>
                  {showAtrasadas && (
                    atrasadas === null ? (
                      <div className="crm-form-hint" style={{ marginTop: 'var(--crm-space-3)' }}>Carregando...</div>
                    ) : atrasadas.length === 0 ? (
                      <div className="crm-form-hint" style={{ marginTop: 'var(--crm-space-3)' }}>Nenhuma tarefa atrasada</div>
                    ) : (
                      <div style={{ marginTop: 'var(--crm-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
                        {atrasadas.map((t) => (
                          <div
                            key={t.id}
                            className="clickable"
                            style={{ cursor: 'pointer', fontSize: 'var(--crm-text-sm)', padding: 'var(--crm-space-2)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)' }}
                            onClick={() => onNavegar('projetos', { projetoId: t.projeto_id })}
                          >
                            <div>{t.titulo}</div>
                            <div className="crm-form-hint">{t.projeto_nome} — venceu em {fmtDate(t.data_vencimento)}</div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </Panel>
              )}
              {estoque && (
                <Panel title="Estoque">
                  <KpiGrid>
                    <KpiCard label="Itens cadastrados" value={estoque.itens_cadastrados} />
                    <KpiCard label="Estoque baixo" value={estoque.estoque_baixo} tone={estoque.estoque_baixo > 0 ? 'warning' : undefined} />
                    <KpiCard label="Movimentações hoje" value={estoque.movimentacoes_hoje} />
                  </KpiGrid>
                </Panel>
              )}
              {patrimonio && (
                <Panel title="Patrimônio">
                  <KpiGrid>
                    <KpiCard label="Em manutenção" value={patrimonio.em_manutencao} tone={patrimonio.em_manutencao > 0 ? 'warning' : undefined} />
                    <KpiCard label="Defeituosos" value={patrimonio.defeituosos} tone={patrimonio.defeituosos > 0 ? 'danger' : undefined} />
                    <KpiCard label="Disponíveis" value={patrimonio.disponiveis} />
                  </KpiGrid>
                </Panel>
              )}
            </div>
          </div>
        </>
      )}

      <Panel title="Atividade recente" flush>
        {atividade === null ? (
          <EmptyState title="Carregando..." />
        ) : atividade.length === 0 ? (
          <EmptyState title="Nenhuma atividade ainda" />
        ) : (
          <div className="crm-timeline">
            {atividade.map((a) => (
              <div className="crm-timeline-item" key={a.id}>
                <Badge tone={categoriaAtividade(a.action)}>{a.user_nome || 'Sistema'}</Badge>
                <span className="crm-timeline-desc">{a.description}</span>
                <span className="crm-timeline-when">{fmtDateTime(a.created_em)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

export default Painel;
